<template>
  <section
    class="relative bg-gradient-to-br from-white via-gray-50 to-[#e6f0f4] py-10 sm:py-12 overflow-hidden"
  >
    <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div class="pt-0">
        <div class="mb-8 text-center sm:mb-12">
          <h2
            class="mx-auto max-w-3xl text-[clamp(1.25rem,5.5vw,2.75rem)] font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] animate-slideUp"
          >
            Tudo que você precisa para sua empresa crescer
          </h2>
        </div>

        <div class="mx-auto mt-4 max-w-6xl lg:mt-6">
          <div
            class="hidden overflow-hidden bg-white rounded-3xl shadow-xl lg:block"
          >
            <div
              class="grid lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] items-stretch"
            >
              <div class="relative h-full">
                <img
                  :src="imgFeaturesLarge"
                  alt="Utilizando o Kali Gestão em qualquer lugar."
                  class="object-cover object-center w-full h-full"
                  loading="lazy"
                />
              </div>
              <div
                class="flex items-center px-8 py-8 text-white bg-kali-blue-600 sm:px-10"
              >
                <p
                  class="max-w-md text-base font-light leading-7 -translate-y-2 sm:-translate-y-3 sm:text-lg md:text-xl sm:leading-8 text-white/95"
                >
                  Transforme a gestão da sua empresa com o Kali Gestão. Um
                  sistema completo, intuitivo e 100% online que centraliza
                  vendas, estoque, finanças e emissão fiscal em um só lugar.
                  Reduza tarefas manuais, ganhe tempo e tome decisões com mais
                  confiança.
                </p>
              </div>
            </div>
          </div>

          <div class="lg:hidden">
            <div
              class="overflow-hidden bg-white rounded-3xl shadow-xl flex flex-col"
            >
              <div class="relative h-80 sm:h-96 md:h-[420px]">
                <img
                  :src="imgFeatures"
                  alt="Utilizando o Kali Gestão em qualquer lugar."
                  class="object-cover object-top w-full h-full"
                  loading="lazy"
                />
              </div>
              <div
                class="bg-kali-blue-600 text-white px-6 py-6 sm:px-8 sm:py-8"
              >
                <p
                  class="text-base font-light leading-7 sm:text-lg sm:leading-8 text-white/95"
                >
                  Transforme a gestão da sua empresa com o Kali Gestão. Um
                  sistema completo, intuitivo e 100% online que centraliza
                  vendas, estoque, finanças e emissão fiscal em um só lugar.
                  Reduza tarefas manuais, ganhe tempo e tome decisões com mais
                  confiança.
                </p>
              </div>
            </div>
          </div>

          <div class="relative z-10 mt-10 sm:mt-12 lg:-mt-40 xl:-mt-44">
            <div
              class="grid grid-cols-1 gap-6 mx-auto max-w-5xl sm:grid-cols-2 lg:grid-cols-4"
            >
              <article
                v-for="feature in features"
                :key="feature.title"
                class="flex flex-col gap-3 p-6 bg-white rounded-2xl border border-gray-100 shadow-md group aspect-square"
              >
                <div
                  class="flex justify-center items-center w-10 h-10 bg-gray-100 rounded-lg transition-all duration-300 text-kali-blue-500 group-hover:scale-105 group-hover:shadow-md group-hover:text-kali-blue-600"
                >
                  <svg
                    v-if="feature.icon === 'training'"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    class="feature-icon"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M12 14l9-5-9-5-9 5 9 5z" />
                    <path d="M12 14v6" />
                    <path d="M7 12v5a2 2 0 002 2h6a2 2 0 002-2v-5" />
                  </svg>
                  <svg
                    v-else-if="feature.icon === 'relationship'"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    class="feature-icon"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M7 10a3 3 0 100-6 3 3 0 000 6z" />
                    <path d="M17 10a3 3 0 100-6 3 3 0 000 6z" />
                    <path d="M3.5 18l4.5-3 2 2 4.5-3 6 4" />
                  </svg>
                  <svg
                    v-else-if="feature.icon === 'automation'"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    class="feature-icon"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="3" />
                    <path
                      d="M19.4 15a1.65 1.65 0 01.33 1.82l-1 1.73a1.65 1.65 0 01-1.51.88h-2a1.65 1.65 0 01-1.51-.88l-.17-.29"
                    />
                    <path
                      d="M4.6 15a1.65 1.65 0 00-.33 1.82l1 1.73A1.65 1.65 0 006.78 19h2a1.65 1.65 0 001.51-.88l.17-.29"
                    />
                    <path
                      d="M19.4 9a1.65 1.65 0 00.33-1.82l-1-1.73A1.65 1.65 0 0017.22 5h-2a1.65 1.65 0 00-1.51.88l-.17.29"
                    />
                    <path
                      d="M4.6 9a1.65 1.65 0 01-.33-1.82l1-1.73A1.65 1.65 0 016.78 5h2a1.65 1.65 0 011.51.88l.17.29"
                    />
                  </svg>
                  <svg
                    v-else-if="feature.icon === 'support'"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    class="feature-icon"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M6 18a3 3 0 003 3h3a3 3 0 003-3v-1" />
                    <path d="M6 11V9a6 6 0 0112 0v2" />
                    <path d="M4 11h2v4H4a2 2 0 01-2-2v-1a2 2 0 012-2z" />
                    <path d="M20 11h-2v4h2a2 2 0 002-2v-1a2 2 0 00-2-2z" />
                  </svg>
                  <svg
                    v-else-if="feature.icon === 'analytics'"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    class="feature-icon"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M4 20h16" />
                    <rect x="5" y="11" width="3" height="6" rx="1" />
                    <rect x="10.5" y="8" width="3" height="9" rx="1" />
                    <rect x="16" y="5" width="3" height="12" rx="1" />
                  </svg>
                  <svg
                    v-else-if="feature.icon === 'integrations'"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    class="feature-icon"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="6" cy="6" r="2.5" />
                    <circle cx="18" cy="6" r="2.5" />
                    <circle cx="12" cy="18" r="2.5" />
                    <path d="M7.8 7.8L10.2 16.2" />
                    <path d="M16.2 7.8L13.8 16.2" />
                    <path d="M8.5 6h7" />
                  </svg>
                </div>
                <h3 class="text-base font-semibold text-gray-900">
                  {{ feature.title }}
                </h3>
                <p class="text-sm leading-relaxed text-gray-600">
                  {{ feature.description }}
                </p>
              </article>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import imgFeatures from "@/assets/imgfeatures.webp";
import imgFeaturesLarge from "@/assets/imgfeatures-lg.webp";

export default {
  name: "HomeFeaturesSection",
  data() {
    return {
      imgFeatures,
      imgFeaturesLarge,
      features: [
        {
          icon: "training",
          title: "Treinamento e Consultoria",
          description:
            "Suporte técnico especializado e capacitação para garantir o melhor uso das soluções Kali Gestão no dia a dia.",
        },
        {
          icon: "relationship",
          title: "Relacionamento",
          description:
            "Parceria próxima com sua empresa para entender necessidades, antecipar demandas e apoiar o crescimento.",
        },
        {
          icon: "automation",
          title: "Automação de Processos",
          description:
            "Automatize tarefas repetitivas, reduza erros operacionais e ganhe tempo para focar na estratégia.",
        },
        {
          icon: "support",
          title: "Suporte Técnico",
          description:
            "Atendimento ágil e humanizado para resolver dúvidas, orientar configurações e manter tudo funcionando.",
        },
      ],
    };
  },
};
</script>

<style scoped>
.bg-grid-pattern {
  background-image:
    linear-gradient(to right, #e5e7eb 1px, transparent 1px),
    linear-gradient(to bottom, #e5e7eb 1px, transparent 1px);
  background-size: 24px 24px;
}

.snap-x {
  scroll-snap-type: x mandatory;
}

.snap-start {
  scroll-snap-align: start;
}

@keyframes feature-icon-bounce {
  0% {
    transform: translateY(0) scale(1);
  }

  40% {
    transform: translateY(-3px) scale(1.08);
  }

  70% {
    transform: translateY(1px) scale(0.98);
  }

  100% {
    transform: translateY(0) scale(1);
  }
}

.feature-icon {
  width: 1.5rem;
  height: 1.5rem;
  transition: transform 0.4s ease-out;
}

.group:hover .feature-icon {
  animation: feature-icon-bounce 0.6s ease-out forwards;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-10px);
  }
}

@keyframes blob {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }

  25% {
    transform: translate(20px, -20px) scale(1.1);
  }

  50% {
    transform: translate(-20px, 20px) scale(0.9);
  }

  75% {
    transform: translate(20px, 20px) scale(1.1);
  }
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}

.animate-blob {
  animation: blob 10s infinite;
}

.animation-delay-2000 {
  animation-delay: 2s;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes slideLeft {
  from {
    opacity: 0;
    transform: translateX(-100px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideRight {
  from {
    opacity: 0;
    transform: translateX(100px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.animate-slideLeft {
  animation: slideLeft 1s ease-out forwards;
}

.animate-slideRight {
  animation: slideRight 1s ease-out forwards;
}
</style>
